import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { CustomFieldType, CustomFieldGroupType, LinkedCustomFieldObjectType, LinkedCustomFieldSetObjectType, CUSTOM_FIELDS_SAVED_EVENT, CUSTOM_FIELD_LIMIT_OBJECT_TYPES } from './CustomFieldTypes';
import { useModal } from 'components/modals/ModalContext';
import Dropdown from '../basefields/Dropdown';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import CustomFieldBuilder from './CustomFieldBuilder';
import { saveData } from 'services/api/saveData';
import { fetchData } from 'services/api/fetchData';
import isEqual from 'lodash/isEqual';
import '../../../style/scss/custom-fields.scss';
import { validateCustomFieldEditor } from './customFieldValidations';

interface CustomFieldModalProps {
    linkedObjectType: LinkedCustomFieldObjectType;
    linkedObjectValue?: string;
    viewKey?: string;
    onSuccess?: () => void;
};

const CustomFieldModal: React.FC<CustomFieldModalProps> = ({
    linkedObjectType,
    linkedObjectValue,
    viewKey,
    onSuccess
}) => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { revealModal, closeModal } = useModal();
    const { setUnsavedChanges } = useGlobalContext();
    const [groups, setGroups] = useState<CustomFieldGroupType[]>([]);
    const [fields, setFields] = useState<CustomFieldType[]>([]);
    const [initialGroups, setInitialGroups] = useState<CustomFieldGroupType[]>([]);
    const [initialFields, setInitialFields] = useState<CustomFieldType[]>([]);
    const [selectedLinkedObjectValue, setSelectedLinkedObjectValue] = useState<string | undefined>(linkedObjectValue);
    const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false);
    const [loadingCustomFields, setLoadingCustomFields] = useState(false);
    const [hasRevealedModal, setHasRevealedModal] = useState(false);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Fetch the linked object values
    const { response: fetchedJobWorkflows, loading: jobWorkflowsLoading } = useFetchData({
        apiUrl: 'get_workflow_for_feature_list/job',
        params: useMemo(() => ({ is_active: true }), []),
    });

    const { response: fetchedDealPipelines, loading: dealPipelinesLoading } = useFetchData({
        apiUrl: 'get_pipeline_list',
        params: useMemo(() => ({ is_active: true }), []),
    });

    const { response: fetchedAssetTypes, loading: assetTypesLoading } = useFetchData({
        apiUrl: 'get_asset_type_list',
        params: useMemo(() => ({ deleted: false }), []),
    });

    const { response: fetchedProductCategories, loading: productCategoriesLoading } = useFetchData({
        apiUrl: 'get_productcategory_list',
        params: useMemo(() => ({ is_active: true }), []),
    });

    // Fetch the custom field usage and limit of the environment, the asset
    // and inspection fields belong to other features and have no limit
    const hasFieldLimit = CUSTOM_FIELD_LIMIT_OBJECT_TYPES.includes(linkedObjectType);
    const { response: fieldUsage } = useFetchData({
        apiUrl: hasFieldLimit ? 'get_custom_field_usage' : '',
        params: useMemo(() => ({}), []),
    });

    // Determine wheter the component is fetching linked object values
    const isFetchingObjectValues = (
        jobWorkflowsLoading === 'loading' || 
        dealPipelinesLoading === 'loading' || 
        productCategoriesLoading === 'loading' ||
        assetTypesLoading === 'loading'
    );

    // Reveal the after completed fetches
    useEffect(() => {
        const shouldRevealForNewCustomField = !selectedLinkedObjectValue && !isFetchingObjectValues && !hasRevealedModal;
        const shouldRevealForExistingCustomField = selectedLinkedObjectValue && hasFetchedInitialData && !isFetchingObjectValues && !loadingCustomFields && !hasRevealedModal;

        if (shouldRevealForNewCustomField || shouldRevealForExistingCustomField) {
            revealModal();
            setHasRevealedModal(true);
        }
    }, [selectedLinkedObjectValue, isFetchingObjectValues, hasFetchedInitialData, loadingCustomFields, hasRevealedModal]);

    // Fetch the current data when the linked object is given
    useEffect(() => {
        if (selectedLinkedObjectValue) {
            fetchCurrentData(selectedLinkedObjectValue);
        }
    }, [selectedLinkedObjectValue]);

    // Detect unsaved changes to prevent modal closing
    useEffect(() => {
        if (hasFetchedInitialData && viewKey) {
            const hasChanges = !isEqual(groups, initialGroups) || !isEqual(fields, initialFields);
            setUnsavedChanges(viewKey, hasChanges);
        }
    }, [groups, fields, initialGroups, initialFields, hasFetchedInitialData]);

    // Fetch the current data
    const fetchCurrentData = async (linkedObjectValue: string) => {

        // Start loading
        setLoadingCustomFields(true);

        try {
            // Fetch the data
            const response = await fetchData({
                apiUrl: `get_custom_fields/${linkedObjectType}/${linkedObjectValue}`,
                handleLogout
            });

            // Set the groups and fields with the response data
            setGroups(response.groups || []);
            setFields(response.fields || []);
            setInitialGroups(response.groups || []);
            setInitialFields(response.fields || []);
            
            // Create the first field if no custom fields exists
            if ((!response.fields || response.fields.length === 0)) {
                const newField: CustomFieldType = {
                    uuid: uuidv4(),
                    linked_object_type: linkedObjectType,
                    linked_object_value: linkedObjectValue,
                    group_id: null,
                    parent_field_id: null,
                    label: '',
                    description: '',
                    placeholder: '',
                    field_type: 'input',
                    required: false,
                    hide_externally: false,
                    ordering: 1,
                    options: [],
                };

                setFields([newField]);
            }

            setHasFetchedInitialData(true);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingCustomFields(false);
        }
    };

    // Set the linked object type configs
    const linkedObjectTypeConfig: Record<LinkedCustomFieldSetObjectType, {
        object: string;
        supportsGroups: boolean;
        options: {
            id: string | number;
            name: string;
        }[];
    }> = {
        job_workflow: {
            object: 'jobs',
            supportsGroups: false,
            options: fetchedJobWorkflows?.results ?? [],
        },
        // job_workflow_appointment: {
        //     object: 'appointments',
        //     supportsGroups: true,
        //     options: fetchedJobWorkflows?.results ?? [],
        // },
        contact_entity_type: {
            object: 'contacts',
            supportsGroups: false,
            options: [
                {
                    id: 'person',
                    name: 'Persoon',
                },
                {
                    id: 'organization',
                    name: 'Organisatie',
                },
            ],
        },
        deal_pipeline: {
            object: 'deals',
            supportsGroups: false,
            options: fetchedDealPipelines?.results ?? [],
        },
        // product_category: {
        //     object: 'Products',
        //     supportsGroups: true,
        //     options: fetchedProductCategories?.results ?? [],
        // },
        asset_type: {
            object: 'assets',
            supportsGroups: true,
            options: fetchedAssetTypes?.results ?? [],
        },
        asset_type_inspection: {
            object: 'assets',
            supportsGroups: false,
            options: fetchedAssetTypes?.results ?? [],
        },
    };

    const isLinkedCustomFieldSetObjectType = (value?: LinkedCustomFieldObjectType): value is LinkedCustomFieldSetObjectType => {
        return [
            'job_workflow',
            'job_workflow_appointment',
            'contact_entity_type',
            'deal_pipeline',
            'product_category',
            'asset_type',
            'asset_type_inspection',
        ].includes(value || '');
    };

    // Get the selected linked object config
    const selectedConfig = isLinkedCustomFieldSetObjectType(linkedObjectType) ? linkedObjectTypeConfig[linkedObjectType] : null;

    // Determine wheter the selected feature supports custom field groups
    const supportsGroups = selectedConfig?.supportsGroups ?? false;

    // Get the linked object options
    const linkedObjectOptions = selectedConfig?.options ?? [];

    // Determine the number of active fields of the environment after this save,
    // the fields of this set are replaced by the fields currently in the editor
    const fieldLimit: number | null = hasFieldLimit ? (fieldUsage?.limit ?? null) : null;
    const initialActiveFields = initialFields.filter(custom_field => !custom_field.deleted).length;
    const currentActiveFields = fields.filter(custom_field => !custom_field.deleted).length;
    const activeFieldsAfterSave = (fieldUsage?.count ?? 0) - initialActiveFields + currentActiveFields;

    // Determine whether another field may be added within the limit
    const canAddField = fieldLimit === null || activeFieldsAfterSave < fieldLimit;

    // Handle submit
    const handleSubmit = async () => {

        // Validate the required fields
        const errors = validateCustomFieldEditor({groups, fields, supportsGroups, t});
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Otherwise reset errors
        setValidationErrors([]);

        // Start loading
        setButtonLoader(true);

        try {
            // Submit parent fields before their conditional questions so new parents resolve
            const submitFields = [...fields].sort((firstField, secondField) =>
                Number(Boolean(firstField.parent_field_id)) - Number(Boolean(secondField.parent_field_id))
            );

            // Save the data
            await saveData({
                apiUrl: 'save_custom_fields',
                data: {
                    groups,
                    fields: submitFields,
                },
            });

            // Notify the usage counter that the custom fields changed
            window.dispatchEvent(new CustomEvent(CUSTOM_FIELDS_SAVED_EVENT));

            // Execute the on success callback
            if (onSuccess) {
                onSuccess();
            }

            // Close the modal
            closeModal();
        } catch (error: any) {
            console.error(error);

            // Show the limit error when the server rejected the save
            if (error?.response?.data?.error === 'custom_field_limit_reached') {
                setValidationErrors([
                    t('custom_field.general.field_limit_error', { limit: error.response.data.limit })
                ]);
            }
        } finally {
            setButtonLoader(false);
        }
    };

    return (
        <div className='custom-field-modal'>
            <div className='modal-body'>
                <div className='form-group'>
                    <label>
                        {t('custom_field.general.select_object_type_label', { objectType: t(`custom_field.object_type.${linkedObjectType}`) } )}
                    </label>
                    <Dropdown<any>
                        name='linked_object'
                        options={linkedObjectOptions}
                        selectedOption={
                            linkedObjectOptions.find((option: any) =>
                                String(option.id) === selectedLinkedObjectValue
                            ) ?? null
                        }
                        optionFormat='name'
                        selectionFormat={(option) => option.name}
                        allowNoneOption={false}
                        showSearch={false}
                        onChange={(value) => {
                            const selectedOption =
                                linkedObjectOptions.find((option: any) =>
                                    String(option.id) === String(value)
                                );

                            if (selectedOption) {
                                setSelectedLinkedObjectValue(String(selectedOption.id));
                                setGroups([]);
                                setFields([]);
                                setHasFetchedInitialData(false);
                            }
                        }}
                    />
                </div>
            </div>

            {selectedLinkedObjectValue && hasFetchedInitialData && !loadingCustomFields && (
                <>
                    <CustomFieldBuilder
                        groups={groups} 
                        setGroups={setGroups} 
                        fields={fields} 
                        setFields={setFields} 
                        supportsGroups={supportsGroups}
                        extendedOptionsFeature={linkedObjectType === 'asset_type_inspection' ? 'forms_extended' : 'custom_fields_extended'}
                        selectedLinkedObjectType={linkedObjectType}
                        selectedLinkedObjectValue={selectedLinkedObjectValue}
                        showFieldPreview={true}
                        canAddField={canAddField}
                        fieldLimitMessage={fieldLimit !== null ? t('custom_field.general.field_limit_reached', { limit: fieldLimit }) : undefined}
                    />

                    <div className='footer'>
                        <div className='left-items'>
                            {validationErrors.length > 0 && (
                                <div className='custom-field-validation-errors'>
                                    {validationErrors.map((validation_error, index) => (
                                        <p key={index}>
                                            {validation_error}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className='buttons-right'>
                            <SecondaryButton
                                onClick={() => closeModal()}
                                size="small"
                                label='general.cancel'
                            />
                            <PrimaryButton
                                onClick={handleSubmit}
                                size="small"
                                label='general.save'
                                loading={buttonLoader}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CustomFieldModal;